import { FormSelectField, SelectProps } from 'omakase';
import { useFeatureFlagForConsumer } from 'featureFlags/useConsumerFeatureFlag';
import IsoCountryCodes from 'constants/IsoCountryCodes';
import phoneCountryCodes, {
  SANCTIONED_PHONE_COUNTRY_CODES_SET,
} from '@tock/shared/constants/phoneCountryCodes';

export const SANCTIONED_ISO_COUNTRY_CODES_SET = new Set<string>(
  phoneCountryCodes
    .filter(({ phone }) => SANCTIONED_PHONE_COUNTRY_CODES_SET.has(phone))
    .map(({ iso }) => iso)
);

type FormIsoCountryCodeFieldOwnProps = {
  disallowSanctionedCodes?: boolean;
};

type FormIsoCountryCodeFieldProps = FormIsoCountryCodeFieldOwnProps & Omit<SelectProps, 'children'>;

/**
 * Formik-ready select field that includes all ISO country codes as options
 */
const FormIsoCountryCodeField: React.FunctionComponent<FormIsoCountryCodeFieldProps> = ({
  name = 'isoCountryCode',
  id = 'isoCountryCode',
  label = 'Country',
  inputProps = {
    'data-testid': 'country-code',
    autoComplete: 'country',
  },
  fullWidth = true,
  placeholder = 'Select a country',
  disallowSanctionedCodes = false,
  ...rest
}) => {
  const canDisallowSanctionedCodes = useFeatureFlagForConsumer(
    'DISALLOW_FRONTEND_SANCTIONED_PHONE_COUNTRY_CODE_SELECTION'
  );
  disallowSanctionedCodes = disallowSanctionedCodes && canDisallowSanctionedCodes;

  return (
    <FormSelectField
      fullWidth={fullWidth}
      name={name}
      id={id}
      label={label}
      inputProps={inputProps}
      placeholder={placeholder}
      {...rest}
    >
      {IsoCountryCodes.map(({ value, key, label }) => (
        <option
          value={value}
          key={key}
          disabled={disallowSanctionedCodes && SANCTIONED_ISO_COUNTRY_CODES_SET.has(value)}
        >
          {label}
        </option>
      ))}
    </FormSelectField>
  );
};

export default FormIsoCountryCodeField;
